// Promote Your Product's form (live's `promote-your-product-form` library view, spec G14-templates
// PR 15): the AI Actor and its voice, the direction, the script (Generate Script, Enhance Script),
// an optional voice preview (Generate Audio), and in the footer the product and the subtitles.
// One tile: the server runs the Advanced talking head, then its captions.
import { Box, CloseButton, Group, Stack, Text, Textarea, Tooltip, UnstyledButton } from "@mantine/core";
import { Archive as ProductIcon } from "lucide-react";
import { useState } from "react";

import { useGetCatalog, useListTemplateCaptionStyles } from "../../../api/generated/endpoints/generation/generation";
import type { Catalog, PromoteExampleProduct, ReferenceSource, Voice } from "../../../api/generated/model";
import { VideoTemplateQuote, VideoTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { AddActorsDialog } from "../../../platform/actors/AddActorsDialog";
import { ActorsComposer } from "../../../platform/actors/composer/ActorsComposer";
import { pickedOfActor, type PickedActor } from "../../../platform/actors/picked";
import { useMakings, type Making } from "../../../platform/actors/use-makings";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { ProductListPopover, type ListedProduct } from "../../../platform/media/ProductListPopover";
import { ScriptActions, ScriptBusy } from "../../../platform/scripts/ScriptActions";
import { useScriptJobs } from "../../../platform/scripts/use-script-jobs";
import { useVoicePages } from "../../../platform/voices/VoiceList";
import { VoicePanel } from "../../../platform/voices/VoicePanel";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ActorTile } from "./ActorTile";
import classes from "./forms.module.css";
import { HandoffNotice } from "./HandoffNotice";
import { ReuseSwitch, StartArrow, type FormExample, type TemplateFormProps } from "./kit";
import { ScriptOverlayWithProduct } from "./ScriptOverlayWithProduct";
import { Subtitles, type SubtitlesValue } from "./Subtitles";
import { NEEDS_SCRIPT, NEEDS_VOICE, useVoiceover, VOICEOVER_MODEL, VoiceAudioTile, VoiceoverButton } from "./voiceover";

/** Live's words (`library-view/constants.ts`, `schema.ts`, `submit-button`), verbatim. */
const ACTOR_TIP =
  "The on-screen presenter of your video. Pick an AI Actor from your library or the global " +
  "catalog — the same person appears in every shot.";
const VOICE_TIP =
  "The voice your actor speaks with. It is used in every shot, so the whole video sounds like " +
  "one person.";
const PRODUCT_TIP =
  "The product your actor promotes. Pick it from your team's product list — without one, the " +
  "video shows no product.";
const SUBTITLES_TIP =
  "Burned-in captions: turn them on or off, pick where they sit and how they look.";
const DIRECTION_PLACEHOLDER = "Describe the overall vibe and direction for the video.";
const SCRIPT_PLACEHOLDER =
  "Write your script... Use [emotion] to add emotions like [laughs], [sights], [excited]";
const BRIEF_PLACEHOLDER =
  "Describe what your actor should say — the product, the audience, the tone…";
const HANDOFF = "Your video is finishing in My Generations — you can close this panel now.";
const NEEDS_ACTOR = "Add an AI Actor";
const NEEDS_DIRECTION = "Describe the direction of the video";
const WAITING_FOR_MODELS = "Waiting for the generation models";
/**
 * Ours (G14 Promote Your Product-2): the run's talking head takes a script of 10 characters or
 * more (G8-talking-head-advanced difference 11), where live's form asks for one character.
 */
const SCRIPT_MIN = 10;
const SCRIPT_TOO_SHORT = `Write a script of at least ${SCRIPT_MIN} characters`;
/** The run's bounds (ours: live's form bounds them in its schema alone, with no word on its start). */
const DIRECTION_MAX = 2_000;
const SCRIPT_MAX = 10_000;
const DIRECTION_TOO_LONG = "Keep the direction within 2,000 characters";
const SCRIPT_TOO_LONG = "Keep the script within 10,000 characters";
/**
 * The minimum counts characters as the server does (code points); the maximums count as the
 * form's own schema does (UTF-16 units, zod's `max`), so the line shows wherever the start is
 * held back. A text of astral characters near a bound is held to that stricter count (G14).
 */
const lengthOf = (text: string) => [...text].length;
/** The chain's models (`usePromoteChainModels`): the talking video's row and the keyframes'. */
const TALKING_VIDEO_TYPE = "video.talking_head_advanced";
const KEYFRAME_MODEL = "gemini-3.1-flash-image";

/** The voice the actor speaks with: one picked in Voice Settings, or the actor's own. */
type VoiceChoice = Pick<Voice, "id" | "name" | "kind">;

/** The AI Actor: a pick, or the card's example portrait (issue 749), which no list holds. */
type PromoteActor = Omit<PickedActor, "source"> & { source: ReferenceSource };

const DEFAULT_SUBTITLES: SubtitlesValue = { on: true, position: "bottom", styleId: "" };

/** The form's values from the card's example (live's `toPromoteExampleValues`, issue 749). */
interface Values {
  actor: PromoteActor;
  /** Live's example origin: the portrait's written description, which the shot list reads. */
  actorDescription: string;
  /** The example's template voice; none where the card does not serve it (live drops it). */
  voice: VoiceChoice | null;
  direction: string;
  script: string;
  /** Live's example product (its `PRODUCT_SOURCES.EXAMPLE`): data and a picture, no row. */
  exampleProduct: PromoteExampleProduct | null;
  subtitles: SubtitlesValue;
}

/**
 * The card's example as the form fills it (live's `toPromoteExampleValues`): the example portrait
 * with its written description, its voice, the direction and the script, its product given as
 * data, and the subtitles; none for another template's.
 */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "promote-your-product") return null;
  const { actor } = inputs;
  const description = inputs.actor_description ?? null;
  if (actor.kind !== "example" || description === null) return null;
  const voice = example.voices.find((each) => each.id === inputs.voice_id);
  return {
    actor: {
      key: actor.key,
      name: voice?.name ?? "Example",
      imageUrl: example.urls[actor.key] ?? null,
      thumbUrl: null,
      screenUrl: null,
      voice: null,
      videoUrl: null,
      source: actor,
    },
    actorDescription: description,
    voice: voice === undefined ? null : { id: voice.id, name: voice.name, kind: "global" },
    direction: inputs.direction,
    script: inputs.script,
    exampleProduct: inputs.product ?? null,
    subtitles: {
      on: inputs.subtitles ?? true,
      position: inputs.caption_position ?? "bottom",
      styleId: inputs.caption_style ?? "",
    },
  };
}

/** Live's one line for the chain's models the team is not offered (`usePromoteChainModels`). */
function missingModels(catalog: Catalog): string | null {
  const missing = [
    ...(catalog.video_models.some((each) => each.job_types.includes(TALKING_VIDEO_TYPE)) ? [] : ["talking-video"]),
    ...(catalog.image_models.some((each) => each.id === KEYFRAME_MODEL) ? [] : ["keyframe image"]),
  ];
  return missing.length === 0 ? null : `No ${missing.join(", ")} model is available for this team`;
}

/**
 * Live's form: "Reuse example" (on fills the card's example: Astrid's portrait with its written
 * description, her voice, the texts, the product given as data and the subtitles; off clears the
 * form; Replicate opens it on; issue 749), the AI Actor tile (From Library, Add AI Actor; a pick
 * replaces the example's portrait) and the voice tile (Voice Settings; once made, the preview's player),
 * the direction, the script with its two tools, Generate Audio (a preview, which the video never
 * uses), and in the footer the product, the subtitles (on, Bottom, Bold cinematic by default)
 * and the round start, its tooltip naming what is missing, as live's. While the preview is made
 * or the run starts, the form is locked, as live's (`isBusy`).
 */
export function PromoteForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [actor, setActor] = useState<PromoteActor | null>(start?.actor ?? null);
  const [actorDescription, setActorDescription] = useState<string | null>(start?.actorDescription ?? null);
  const [voice, setVoice] = useState<VoiceChoice | null>(start?.voice ?? null);
  const [direction, setDirection] = useState(start?.direction ?? "");
  const [script, setScript] = useState(start?.script ?? "");
  const [product, setProduct] = useState<ListedProduct | null>(null);
  const [exampleProduct, setExampleProduct] = useState<PromoteExampleProduct | null>(start?.exampleProduct ?? null);
  const [subtitles, setSubtitles] = useState<SubtitlesValue>(start?.subtitles ?? DEFAULT_SUBTITLES);
  const [library, setLibrary] = useState(false);
  const [makingActor, setMakingActor] = useState(false);
  const [makings, setMakings] = useState<Making[]>([]);
  const [voicePanel, setVoicePanel] = useState(false);
  const [scriptOpen, setScriptOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [handoffShown, setHandoffShown] = useState(false);
  const catalog = useGetCatalog().data;
  const styles = useListTemplateCaptionStyles(teamId).data;
  const styleId = subtitles.styleId || (styles?.default ?? "");
  // Only for Voice Settings' "Recommended Voice" row, which plays the actor's own voice.
  const globals = useVoicePages(teamId, brandId, { kind: "global" });
  const voices = globals.data?.pages.flatMap((page) => page.items) ?? [];
  const recommended = actor?.voice == null ? null : (voices.find((each) => each.id === actor.voice?.id) ?? null);
  const preview = useVoiceover(teamId, brandId);
  // A new script or another voice is not what the preview says: it goes, as live's
  // `useInvalidateAudio`; the same voice picked again keeps it.
  const write = (text: string) => {
    setScript(text);
    preview.clear();
  };
  const chooseVoice = (next: VoiceChoice | null) => {
    if (next?.id !== voice?.id) preview.clear();
    setVoice(next);
  };
  // Live's actor pick (`actor-tile-field`): an actor with its own voice brings it, whatever page
  // of voices it is on; one without keeps the voice chosen. A pick replaces the example's
  // portrait, and its description goes with it.
  const chooseActor = (next: PickedActor | null) => {
    setActor(next);
    setActorDescription(null);
    if (next?.voice != null) chooseVoice({ id: next.voice.id, name: next.voice.name, kind: "global" });
  };
  // Live's "Reuse example": on fills the card's example, off clears the form.
  const fill = (on: boolean) => {
    const values = on ? valuesOf(example) : null;
    setReuse(values !== null);
    setActor(values?.actor ?? null);
    setActorDescription(values?.actorDescription ?? null);
    chooseVoice(values?.voice ?? null);
    setDirection(values?.direction ?? "");
    write(values?.script ?? "");
    setProduct(null);
    setExampleProduct(values?.exampleProduct ?? null);
    setSubtitles(values?.subtitles ?? DEFAULT_SUBTITLES);
  };
  const pickProduct = (picked: ListedProduct | null) => {
    setProduct(picked);
    setExampleProduct(null);
  };
  // The example's voice is the card's own (G14 Actor Swap-1): only the card's run speaks with it,
  // so its preview, a speech of its own, cannot (ours; live's previews any voice).
  const templateVoice = voice !== null && example?.voices.some((each) => each.id === voice.id) === true;
  const productName = product?.name ?? exampleProduct?.name ?? null;
  useMakings({
    teamId,
    brandId,
    makings,
    onMade: (jobId, made) => {
      setMakings((current) => current.filter((each) => each.jobId !== jobId));
      chooseActor(pickedOfActor(made));
    },
    onFailed: (jobId) =>
      setMakings((current) => current.map((each) => (each.jobId === jobId ? { ...each, failed: true } : each))),
  });
  const scripts = useScriptJobs({ teamId, brandId, tool: "templates", onScript: write });
  const busyScript = scripts.processing || scripts.enhancing;
  const modelsMissing = catalog === undefined ? null : missingModels(catalog);
  const previewReady = catalog?.audio_models.some((each) => each.id === VOICEOVER_MODEL) === true;
  // Live trims the direction and the script before it sends them (`usePromoteFormValue`).
  const shown = { direction: direction.trim(), script: script.trim() };
  const missing = [
    ...(actor === null ? [NEEDS_ACTOR] : []),
    ...(voice === null ? [NEEDS_VOICE] : []),
    ...(shown.direction === "" ? [NEEDS_DIRECTION] : []),
    ...(shown.direction.length > DIRECTION_MAX ? [DIRECTION_TOO_LONG] : []),
    ...(shown.script === "" ? [NEEDS_SCRIPT] : lengthOf(shown.script) < SCRIPT_MIN ? [SCRIPT_TOO_SHORT] : []),
    ...(shown.script.length > SCRIPT_MAX ? [SCRIPT_TOO_LONG] : []),
    ...(catalog === undefined || modelsMissing !== null ? [WAITING_FOR_MODELS] : []),
  ];
  const inputs =
    actor === null || voice === null
      ? null
      : {
          template: "promote-your-product" as const,
          actor: actor.source,
          voice_id: voice.id,
          ...(actorDescription === null ? {} : { actor_description: actorDescription }),
          direction: shown.direction,
          script: shown.script,
          ...(product !== null
            ? { product_id: product.productId }
            : exampleProduct !== null
              ? { product: exampleProduct }
              : {}),
          subtitles: subtitles.on,
          caption_position: subtitles.position,
          ...(styleId === "" ? {} : { caption_style: styleId }),
        };
  const body = inputs === null ? null : { type: "video.template" as const, inputs };
  const request = VideoTemplateRequest.safeParse(body);
  const quote = VideoTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success && missing.length === 0 ? quote.data : undefined,
    request: request.success && missing.length === 0 ? request.data : undefined,
    onStarted: (started) => {
      setHandoffShown(true);
      onStarted?.(started);
    },
  });
  const busy = preview.running || job.start.isPending;
  if (makingActor && catalog !== undefined) {
    // Live's "Add AI Actor" (`actor-generation-shell`): the actor composer in the form's place.
    const close = () => setMakingActor(false);
    return (
      <ActorsComposer
        teamId={teamId}
        brandId={brandId}
        catalog={catalog}
        onStarted={(started) => {
          setMakings((current) => [...current, { jobId: started.id, failed: false }]);
          close();
        }}
        place={{
          outputs: 1,
          uploadMost: 1,
          onCancel: close,
          onCreated: (made) => {
            const first = made[0];
            if (first !== undefined) chooseActor(pickedOfActor(first));
            close();
          },
        }}
      />
    );
  }
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} locked={busy} />}
      summary={<Text size="sm">Promote Your Product</Text>}
      overlay={
        scriptOpen ? (
          <ScriptOverlayWithProduct
            teamId={teamId}
            brandId={brandId}
            placeholder={BRIEF_PLACEHOLDER}
            pending={scripts.generating}
            error={scripts.generateError}
            product={{ value: product, onChange: pickProduct }}
            onGenerate={(brief, productId) => scripts.generate(brief, productId, () => setScriptOpen(false))}
            onCancel={() => setScriptOpen(false)}
          />
        ) : undefined
      }
      aside={
        voicePanel ? (
          <VoicePanel
            teamId={teamId}
            brandId={brandId}
            chosen={voice}
            recommended={recommended}
            // Locked while a preview is made: another voice would forget a speech being paid for.
            onChoose={(next) => (preview.running ? undefined : chooseVoice(next))}
            onClose={() => setVoicePanel(false)}
            designModel={catalog?.audio_models.find((each) => each.job_types.includes("voice.design"))}
          />
        ) : undefined
      }
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={4} wrap="nowrap">
            <ProductListPopover
              teamId={teamId}
              brandId={brandId}
              chosenId={product?.productId ?? null}
              opened={productsOpen}
              onClose={() => setProductsOpen(false)}
              onPick={pickProduct}
              position="top-start"
              anchor={(wrap) => (
                <Tooltip label={PRODUCT_TIP} multiline maw={280} withArrow disabled={productsOpen}>
                  {wrap(
                    <UnstyledButton
                      className={classes.chip}
                      aria-label={productName === null ? "Product" : `Product: ${productName}`}
                      disabled={busy}
                      onClick={() => setProductsOpen((open) => !open)}
                    >
                      {/* Live's `shared/product-select`: the archive box at 20 px. */}
                      <ProductIcon size={20} strokeWidth={1.5} aria-hidden />
                      {productName === null ? null : <span>{productName}</span>}
                    </UnstyledButton>,
                  )}
                </Tooltip>
              )}
            />
            {productName === null ? null : (
              <CloseButton
                size="xs"
                aria-label="Remove the product"
                disabled={busy}
                onClick={() => pickProduct(null)}
              />
            )}
            <Subtitles
              value={{ ...subtitles, styleId }}
              styles={styles?.styles}
              tip={SUBTITLES_TIP}
              disabled={busy}
              onChange={setSubtitles}
            />
          </Group>
          <Tooltip
            label={missing.map((line) => (
              <Box key={line}>{line}</Box>
            ))}
            disabled={missing.length === 0}
            withArrow
          >
            <span>
              <StartArrow canStart={job.canStart && !busy} starting={job.start.isPending} onStart={job.startJob} />
            </span>
          </Tooltip>
        </Group>
      }
    >
      <Stack gap={12}>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <ActorTile
            actor={actor}
            making={makings[0] ?? null}
            tip={ACTOR_TIP}
            onFromLibrary={() => setLibrary(true)}
            onMakeActor={catalog === undefined ? null : () => setMakingActor(true)}
            onRemove={() => chooseActor(null)}
            onDropMaking={() => setMakings([])}
            disabled={busy}
          />
          <VoiceAudioTile
            voiceName={voice?.name ?? null}
            voiceover={preview}
            tip={VOICE_TIP}
            disabled={job.start.isPending}
            onOpen={() => setVoicePanel(true)}
          />
        </Group>
        <Textarea
          variant="unstyled"
          aria-label="Direction"
          placeholder={DIRECTION_PLACEHOLDER}
          autosize
          minRows={1}
          maxRows={4}
          disabled={busy}
          value={direction}
          onChange={(event) => setDirection(event.currentTarget.value)}
        />
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" fw={500}>
            Script
          </Text>
          <ScriptActions
            canGenerate={!busyScript && !busy}
            canEnhance={!busyScript && !busy && script.trim() !== ""}
            enhancing={scripts.enhancing}
            failed={scripts.scriptFailed}
            onGenerate={() => setScriptOpen(true)}
            onEnhance={() => scripts.enhance(script)}
          />
        </Group>
        <ScriptBusy busy={scripts.processing}>
          <Textarea
            variant="unstyled"
            aria-label="Script"
            placeholder={SCRIPT_PLACEHOLDER}
            autosize
            minRows={2}
            maxRows={8}
            disabled={scripts.processing || busy}
            value={script}
            onChange={(event) => write(event.currentTarget.value)}
          />
        </ScriptBusy>
        <VoiceoverButton
          voiceover={preview}
          script={script}
          voiceId={voice?.id ?? null}
          disabled={busyScript || job.start.isPending || !previewReady || templateVoice}
        />
        {modelsMissing === null ? null : (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {modelsMissing}
          </Text>
        )}
        {preview.error === null ? null : <ErrorAlert error={preview.error} />}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's hand-off line, once the run is the server's (decision 1), until dismissed. */}
        {handoffShown ? <HandoffNotice message={HANDOFF} onDismiss={() => setHandoffShown(false)} /> : null}
      </Stack>
      {library ? (
        <AddActorsDialog
          teamId={teamId}
          brandId={brandId}
          chosen={actor === null || actor.source.kind === "example" ? [] : [{ ...actor, source: actor.source }]}
          max={1}
          onClose={() => setLibrary(false)}
          onConfirm={(picked) => {
            chooseActor(picked[0] ?? null);
            setLibrary(false);
          }}
        />
      ) : null}
    </GlassPanel>
  );
}
